import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type Theme = 'deep-ocean-blue-theme' | 'steel-blue-medical-theme' | 'deep-teal-medical-theme' | 'navy-medical-theme';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly THEME_STORAGE_KEY = 'isamed-theme';
  private readonly DEFAULT_THEME: Theme = 'navy-medical-theme';

  private currentThemeSubject: BehaviorSubject<Theme>;
  public currentTheme$: Observable<Theme>;

  constructor() {
    const savedTheme = this.getSavedTheme();
    this.currentThemeSubject = new BehaviorSubject<Theme>(savedTheme);
    this.currentTheme$ = this.currentThemeSubject.asObservable();
    this.applyTheme(savedTheme);
  }

  getCurrentTheme(): Theme {
    return this.currentThemeSubject.value;
  }

  setTheme(theme: Theme): void {
    this.currentThemeSubject.next(theme);
    this.applyTheme(theme);
    this.saveTheme(theme);
  }

  private applyTheme(theme: Theme): void {
    if (typeof document === 'undefined') {
      return;
    }

    const htmlElement = document.documentElement;

    htmlElement.classList.remove(
      'deep-ocean-blue-theme',
      'steel-blue-medical-theme',
      'deep-teal-medical-theme',
      'navy-medical-theme'
    );

    htmlElement.classList.add(theme);

    void htmlElement.offsetHeight;
  }

  private getSavedTheme(): Theme {
    if (typeof window === 'undefined' || !window.localStorage) {
      return this.DEFAULT_THEME;
    }

    try {
      const saved = localStorage.getItem(this.THEME_STORAGE_KEY);
      if (saved && this.isValidTheme(saved)) {
        return saved as Theme;
      }
    } catch (error) {
    }

    return this.DEFAULT_THEME;
  }

  private saveTheme(theme: Theme): void {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    try {
      localStorage.setItem(this.THEME_STORAGE_KEY, theme);
    } catch (error) {
    }
  }

  private isValidTheme(theme: string): theme is Theme {
    return [
      'deep-ocean-blue-theme',
      'steel-blue-medical-theme',
      'deep-teal-medical-theme',
      'navy-medical-theme'
    ].includes(theme);
  }
}
